/**
 * Stage Gate Kanban Component
 * 
 * Kanban board for tracking proposals through stage gate review process.
 * Based on OpportunityKanban pattern from ExecutiveDashboard.
 */

import React, { useState } from 'react';
import { Card, Badge } from '@tracekit/core';
import { DollarSign, Calendar, Building2, ChevronRight, User } from 'lucide-react';
import { StageGateProposal, StageGateColumn, StageGateStatus } from '../types/stageGate';

interface StageGateKanbanProps {
  proposals: StageGateProposal[];
  onProposalClick?: (reportId: string) => void;
  onStatusChange?: (reportId: string, newStatus: StageGateStatus) => void;
  canChangeStatus?: boolean;
  userRole?: 'viewer' | 'writer' | 'admin';
}

export const StageGateKanban: React.FC<StageGateKanbanProps> = ({
  proposals,
  onProposalClick,
  onStatusChange,
  canChangeStatus = true,
  userRole = 'viewer',
}) => {
  const [draggedProposal, setDraggedProposal] = useState<StageGateProposal | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<StageGateStatus | null>(null);

  // Determine if a status transition is allowed based on user role
  const isStatusTransitionAllowed = (fromStatus: StageGateStatus, toStatus: StageGateStatus): boolean => {
    // Viewers can't change status
    if (userRole === 'viewer') {
      return false;
    }
    
    // Writers can only move between draft and in_review
    if (userRole === 'writer') {
      const allowedTransitions = [
        ['draft', 'in_review'],
        ['in_review', 'draft'],
      ];
      return allowedTransitions.some(([from, to]) => from === fromStatus && to === toStatus);
    }
    
    // Admins can move between any statuses
    if (userRole === 'admin') {
      return true;
    }
    
    return false;
  };

  // Get valid drop targets for a proposal based on user role
  const getValidDropTargets = (proposal: StageGateProposal): StageGateStatus[] => {
    if (userRole === 'viewer') {
      return [];
    }
    
    if (userRole === 'writer') {
      // Writers can only move between draft and in_review
      if (proposal.status === 'draft') {
        return ['in_review'];
      }
      if (proposal.status === 'in_review') {
        return ['draft'];
      }
      return [];
    }
    
    // Admins can move to any status (except the current one)
    const allStatuses: StageGateStatus[] = ['draft', 'in_review', 'approved', 'rejected', 'submitted', 'archived'];
    return allStatuses.filter(status => status !== proposal.status);
  };
  // Define columns for stage gate process
  const columns: StageGateColumn[] = [
    {
      id: 'draft',
      title: 'Draft',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-gray-500',
    },
    {
      id: 'in_review',
      title: 'In Review',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-yellow-500',
    },
    {
      id: 'approved',
      title: 'Approved',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-green-500',
    },
    {
      id: 'submitted',
      title: 'Submitted',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-blue-500',
    },
    {
      id: 'rejected',
      title: 'Rejected',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-red-500',
    },
    {
      id: 'archived',
      title: 'Archived',
      count: 0,
      value: 0,
      proposals: [],
      color: 'bg-slate-500',
    },
  ];

  // Group proposals by status
  proposals.forEach((proposal) => {
    const column = columns.find((col) => col.id === proposal.status);
    if (column) {
      column.proposals.push(proposal);
      column.count++;
      column.value += proposal.value || 0;
    }
  });

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 30) return `${diffDays}d left`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, proposal: StageGateProposal) => {
    if (!canChangeStatus) {
      e.preventDefault();
      return;
    }
    setDraggedProposal(proposal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', proposal.reportId);
    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedProposal(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: StageGateStatus) => {
    if (!canChangeStatus || !draggedProposal) return;
    
    // Check if this transition is allowed for the user's role
    if (!isStatusTransitionAllowed(draggedProposal.status, columnId)) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the column (not just moving to a child)
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e: React.DragEvent, columnId: StageGateStatus) => {
    e.preventDefault();
    if (!draggedProposal || !canChangeStatus) return;

    // Don't update if dropped in the same column
    if (draggedProposal.status === columnId) {
      setDraggedProposal(null);
      setDragOverColumn(null);
      return;
    }

    // Check if this transition is allowed for the user's role
    if (!isStatusTransitionAllowed(draggedProposal.status, columnId)) {
      setDraggedProposal(null);
      setDragOverColumn(null);
      return;
    }

    // Call status change handler
    if (onStatusChange) {
      onStatusChange(draggedProposal.reportId, columnId);
    }

    setDraggedProposal(null);
    setDragOverColumn(null);
  };

  return (
    <div className="w-full">
      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((column) => (
          <div
            key={column.id}
            className="flex-shrink-0 w-80"
            style={{ minWidth: '320px' }}
          >
            {/* Column Header */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <h3 className="text-sm font-semibold text-theme-text">
                    {column.title}
                  </h3>
                  <Badge variant="default" className="text-xs">
                    {column.count}
                  </Badge>
                </div>
              </div>
              <div className="text-xs text-theme-text-secondary font-medium">
                {formatCurrency(column.value)} total value
              </div>
            </div>

            {/* Cards Container */}
            <div
              className={`space-y-3 min-h-[200px] transition-all ${
                dragOverColumn === column.id && 
                draggedProposal && 
                draggedProposal.status !== column.id &&
                isStatusTransitionAllowed(draggedProposal.status, column.id)
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-400 border-dashed rounded-lg p-2'
                  : ''
              } ${
                dragOverColumn === column.id && 
                draggedProposal && 
                draggedProposal.status !== column.id &&
                !isStatusTransitionAllowed(draggedProposal.status, column.id)
                  ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-400 border-dashed rounded-lg p-2'
                  : ''
              }`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {column.proposals.slice(0, 10).map((proposal) => (
                <div
                  key={proposal.id}
                  draggable={canChangeStatus}
                  onDragStart={(e) => handleDragStart(e, proposal)}
                  onDragEnd={handleDragEnd}
                  className={`${
                    canChangeStatus ? 'cursor-move' : 'cursor-pointer'
                  } ${
                    draggedProposal?.id === proposal.id ? 'opacity-50' : ''
                  }`}
                >
                <Card
                  className="p-4 hover:shadow-lg transition-all hover:scale-[1.02] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                  onClick={() => onProposalClick?.(proposal.reportId)}
                >
                  {/* Title */}
                  <h4 className="text-sm font-semibold text-theme-text mb-2 line-clamp-2">
                    {proposal.title}
                  </h4>

                  {/* Client Name */}
                  {proposal.clientName && (
                    <div className="flex items-center gap-1 text-xs text-theme-text-secondary mb-2">
                      <Building2 className="w-3 h-3" />
                      <span className="truncate">{proposal.clientName}</span>
                    </div>
                  )}

                  {/* Value */}
                  {proposal.value && (
                    <div className="flex items-center gap-1 text-sm font-bold text-green-600 dark:text-green-400 mb-2">
                      <DollarSign className="w-4 h-4" />
                      {formatCurrency(proposal.value)}
                    </div>
                  )}

                  {/* Reviewer */}
                  {proposal.currentReviewer && (
                    <div className="flex items-center gap-1 text-xs text-theme-text-secondary mb-2">
                      <User className="w-3 h-3" />
                      <span className="truncate">{proposal.currentReviewer}</span>
                    </div>
                  )}

                  {/* Comments Count */}
                  {proposal.commentsCount !== undefined && proposal.commentsCount > 0 && (
                    <div className="text-xs text-theme-text-secondary mb-2">
                      {proposal.commentsCount} comment{proposal.commentsCount !== 1 ? 's' : ''}
                    </div>
                  )}

                  {/* Deadline */}
                  <div className="flex items-center gap-1 text-xs text-theme-text-secondary">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(proposal.deadline)}</span>
                  </div>

                  {/* Action Arrow */}
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <ChevronRight className="w-4 h-4 text-theme-text-secondary" />
                  </div>
                </Card>
                </div>
              ))}

              {column.proposals.length === 0 && (
                <div className="text-center py-8 text-sm text-theme-text-secondary">
                  No proposals
                </div>
              )}

              {column.proposals.length > 10 && (
                <div className="text-center py-2 text-xs text-theme-text-secondary">
                  +{column.proposals.length - 10} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

